import { Injectable, inject } from '@angular/core';
import { Constants } from '@core/app-constants';
import {
  BasketClient,
  BookBasketSubscriptionSuccessResponseDto,
  BookWithSubscriptionRequestDto,
  GetPaymentModesResponse,
  PaymentModeClient,
  PaymentModeResponseDto,
  SubscriptionBlockResponseDto,
  SubscriptionBlockTypeResponseDto,
  SubscriptionCategoryResponseDto,
  SubscriptionClient,
  SubscriptionContingentResponseDto,
  SubscriptionDetailResponseDto,
  SubscriptionPlanClient,
  SubscriptionPriceResponseDto,
  SubscriptionResponseDto,
} from '@core/services/apiclient';
import { HttpHandlerService } from '@core/services/http-handler.service';
import {
  SalesBookingSummary,
  SalesEventTypeEnum,
  SubscriptionToBookingSummary,
} from '@sales/models/sales.model';
import { Observable, map } from 'rxjs';
import { SalesAbstractService } from './sales-abstract.service';

@Injectable({
  providedIn: 'root',
})
export class SalesSubscriptionService implements SalesAbstractService {
  private readonly subscriptionClient = inject(SubscriptionClient);
  private readonly paymentModeClient = inject(PaymentModeClient);
  private readonly basketClient = inject(BasketClient);
  private readonly subscriptionPlanClient = inject(SubscriptionPlanClient);
  private readonly httpHandlerService = inject(HttpHandlerService);

  _type: SalesEventTypeEnum;

  public getEventSessions(channel: number): Observable<SubscriptionResponseDto[]> {
    return this.subscriptionClient
      .getAvailable(Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SubscriptionResponseDto[]>());
  }

  public getSubscriptionDetails(subscriptionId: number): Observable<SubscriptionDetailResponseDto> {
    return this.subscriptionClient
      .getDetails(subscriptionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SubscriptionDetailResponseDto>());
  }

  public getBlockType(subscriptionId: number): Observable<string> {
    return this.subscriptionPlanClient
      .getSubscriptionSessionPlanType(subscriptionId, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<SubscriptionBlockTypeResponseDto>(),
        map((response: SubscriptionBlockTypeResponseDto) => response?.eventType),
      );
  }

  public getPaymentMode(): Observable<PaymentModeResponseDto[]> {
    return this.paymentModeClient.getPaymentMode(Constants.ApiVersion).pipe(
      this.httpHandlerService.handleApiResponse<GetPaymentModesResponse>(),
      map((response) => response.paymentModes ?? []),
    );
  }

  public getBlocks(
    channelId: number,
    subscriptionId: number,
    contingentId: number,
    categoryId: number,
  ): Observable<SubscriptionBlockResponseDto[]> {
    return this.subscriptionClient
      .getSubscriptionBlocks(subscriptionId, channelId, contingentId, categoryId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SubscriptionBlockResponseDto[]>());
  }

  public getCategories(
    channelId: number,
    subscriptionId: number,
    blockId: number,
    contingentId: number,
  ): Observable<SubscriptionCategoryResponseDto[]> {
    return this.subscriptionClient
      .getSubscriptionCategories(subscriptionId, channelId, contingentId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SubscriptionCategoryResponseDto[]>());
  }

  public getPrices(
    channelId: number,
    subscriptionId: number,
    categoryId: number,
  ): Observable<SubscriptionPriceResponseDto[]> {
    return this.subscriptionClient
      .getSubscriptionPrices(subscriptionId, channelId, categoryId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SubscriptionPriceResponseDto[]>());
  }

  public getContingents(
    subscriptionId: number,
    channelId: number,
  ): Observable<SubscriptionContingentResponseDto[]> {
    return this.subscriptionClient
      .getContingents(subscriptionId, channelId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SubscriptionContingentResponseDto[]>());
  }

  public bookSeats(subscription: BookWithSubscriptionRequestDto): Observable<SalesBookingSummary> {
    return this.basketClient
      .bookSubscriptionSeats(subscription, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<BookBasketSubscriptionSuccessResponseDto>(),
        map(SubscriptionToBookingSummary),
      );
  }

  public unbookSubscriptionSeats(subscriptionIds: number[], basketId: number): Observable<boolean> {
    return this.basketClient
      .unBookSubscriptionSeats(basketId, subscriptionIds, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<boolean>());
  }
}
